import {
  commentsTruncated,
  commentsUnreadable,
  pendingReplyCount,
  pickImage,
  type Comment,
  type CommentsFailure,
  type CommentsState,
  type CommentSort,
  type CommentThread,
  type ImageRef,
} from '@visious/core';
import { fmt, text } from '../../strings';
import { toRichNodeViews, type RichNodeView } from '../../richText/model';
import { channelHrefOf } from './model';

/**
 * §6/§8's comment section, as props (R13). The *sequencing* — which page to ask for, what a failed
 * page means, when a fallback is allowed — lives in core's `comments/threads.ts`; what lives here
 * is the part that is about this platform's screen: formatted counts, resolved hrefs, and the
 * sentences the reader gets when the list is short or absent.
 */

export interface CommentView {
  readonly id: string;
  readonly author: string;
  readonly authorHref: string | null;
  readonly authorAvatarUrl: string | null;
  /** For the placeholder that stands in for a missing avatar — which is a *normal* state (§6). */
  readonly authorInitial: string;
  readonly verified: boolean;
  readonly isChannelOwner: boolean;
  readonly isSponsor: boolean;
  readonly isPinned: boolean;
  readonly isEdited: boolean;
  readonly bodyNodes: readonly RichNodeView[];
  readonly likeCountText: string | null;
  readonly likeCountTitle: string | null;
  readonly publishedText: string | null;
  readonly hearted: boolean;
  /** Still needed with the handle off the screen: it is the spoken half and the hover title. */
  readonly heartedBy: string | null;
  /**
   * The hearting channel's avatar, which is what the footer *shows* now. `null` is a normal
   * state — `creatorHeart` carries `creatorName` without a `creatorThumbnail` often enough — and the
   * heart alone then says it, exactly as it did when the name was missing.
   */
  readonly heartedByAvatarUrl: string | null;
}

export interface ThreadView {
  readonly comment: CommentView;
  readonly replies: readonly CommentView[];
  readonly expanded: boolean;
  readonly loading: boolean;
  readonly failed: boolean;
  /** `null` when the thread has no replies at all — the button is then not rendered. */
  readonly toggleLabel: string | null;
  /** Set when more reply pages exist behind the ones already loaded. */
  readonly moreLabel: string | null;
}

/**
 * A remark above the list. Every one of these exists because the alternative is a screen that
 * quietly misreports what the reader is looking at.
 */
export interface CommentsNotice {
  readonly text: string;
  readonly tone: 'info' | 'warning';
}

export interface CommentsView {
  readonly heading: string;
  readonly sort: CommentSort;
  readonly sortOptions: readonly { readonly id: CommentSort; readonly label: string; readonly selected: boolean }[];
  readonly threads: readonly ThreadView[];
  readonly notice: CommentsNotice | null;
  readonly phase: CommentsState['phase'];
  readonly loadingMore: boolean;
  readonly canLoadMore: boolean;
  readonly emptyText: string | null;
  /** The whole list failed — there is nothing on screen behind this. */
  readonly failureText: string | null;
  /** A *further* page failed. The comments already loaded are still there, and still correct. */
  readonly loadMoreFailureText: string | null;
  readonly retryable: boolean;
}

/**
 * The smallest avatar that is still ≥ 40 px — same rule as the byline's, one size down because a
 * comment avatar is smaller. `pickImage` falls back to the largest offered rather than to nothing.
 */
function avatarUrlOf(images: readonly ImageRef[]): string | null {
  return pickImage(images, { minWidth: 40 })?.url ?? null;
}

/** An avatar-less comment is normal (§6 — the array is intermittently empty for a whole page). */
function initialOf(author: string): string {
  const trimmed = author.replace(/^@/, '').trim();
  return trimmed === '' ? '?' : (trimmed[0] ?? '?').toUpperCase();
}

function toCommentView(comment: Comment, nowEpochSec: number): CommentView {
  return {
    id: comment.commentId,
    author: comment.author,
    // The byline's own resolver, which prefers `authorId` and *parses* the fallback rather than
    // trusting it (`links/routes.ts`). A `/@handle` still links; a bare `/channel/` does not.
    authorHref: channelHrefOf(comment),
    authorAvatarUrl: avatarUrlOf(comment.authorThumbnails),
    authorInitial: initialOf(comment.author),
    verified: comment.authorVerified,
    isChannelOwner: comment.authorIsChannelOwner,
    isSponsor: comment.isSponsor,
    isPinned: comment.isPinned,
    isEdited: comment.isEdited,
    bodyNodes: toRichNodeViews(comment.bodyNodes),
    likeCountText: fmt().count(comment.likeCount),
    likeCountTitle: fmt().exactCount(comment.likeCount),
    publishedText: fmt().published(comment.publishedEpochSec, nowEpochSec),
    hearted: comment.creatorHeart !== null,
    heartedBy: comment.creatorHeart?.creatorName ?? null,
    // A single `ImageRef`, not a list — `creatorThumbnail` is one URL (`parse/comments.ts`), so there
    // is nothing for `pickImage` to choose between.
    heartedByAvatarUrl: comment.creatorHeart?.thumbnail?.url ?? null,
  };
}

function toThreadView(thread: CommentThread, nowEpochSec: number): ThreadView {
  const promised = thread.comment.replies?.replyCount ?? null;
  const expanded = thread.status === 'open' || thread.status === 'loading';
  const pending = pendingReplyCount(thread);

  const words = text().comments.replies;
  const toggleLabel =
    thread.continuation === null && thread.replies.length === 0
      ? null
      : expanded
        ? words.hide
        : promised === null
          ? words.show
          : words.count(fmt().countedNoun(promised, words.noun) ?? String(promised));

  return {
    comment: toCommentView(thread.comment, nowEpochSec),
    replies: thread.replies.map((reply) => toCommentView(reply, nowEpochSec)),
    expanded,
    loading: thread.status === 'loading',
    failed: thread.status === 'failed',
    toggleLabel,
    // Only once something is on screen: a thread that has loaded nothing yet says "12 replies", not
    // "12 replies" *and* "load 12 more".
    moreLabel:
      thread.continuation === null || thread.replies.length === 0
        ? null
        : pending === null || pending === 0
          ? words.showMore
          : words.showMoreCount(fmt().countedNoun(pending, words.noun) ?? String(pending)),
  };
}

/**
 * The remark above the list. Two of the three are the visible half of the slice's central decision:
 * a substituted ordering and a truncated page are both states in which the list on screen is not
 * the list the reader asked for, and neither of them is an error the API reports.
 */
function noticeOf(state: CommentsState): CommentsNotice | null {
  const words = text().comments.notice;
  const total = state.commentCount === null ? words.unknownTotal : String(state.commentCount);

  if (state.sortFallbackFrom !== null && state.phase === 'ready') {
    return {
      tone: 'warning',
      text: commentsTruncated(state)
        ? words.sortFallbackTruncated(state.threads.length, total)
        : words.sortFallback,
    };
  }
  if (commentsTruncated(state)) {
    return { tone: 'info', text: words.truncated(state.threads.length, total) };
  }
  return null;
}

function failureTextOf(failure: CommentsFailure | null): string | null {
  if (failure === null) return null;
  const words = text().comments.failure;
  switch (failure.kind) {
    case 'absent':
      return null;
    case 'upstream-broken':
      // Both orders were refused. Naming Invidious rather than "something went wrong" is the point:
      // nothing the reader does will change it, and it is not this app failing.
      return words.upstreamBroken;
    case 'error':
      return failure.retryable ? words.errorRetryable : words.error;
  }
}

function emptyTextOf(state: CommentsState, live: boolean): string | null {
  const words = text().comments.empty;
  if (state.phase === 'absent') {
    // Measured: every live stream sampled (6 of 6) answers `404 {"error":"Comments not found."}`,
    // because a live video has a chat rather than comments. Saying which of the two it is needs a
    // fact about the *video*, which the endpoint's own answer does not carry.
    return live ? words.live : words.disabled;
  }
  if (state.phase !== 'empty') return null;
  return commentsUnreadable(state) ? words.unreadable : words.none;
}

export interface CommentsViewOptions {
  readonly nowEpochSec: number;
  /** Only used to name the reason a live stream has no comments — see {@link emptyTextOf}. */
  readonly live: boolean;
}

export function toCommentsView(state: CommentsState, options: CommentsViewOptions): CommentsView {
  const nowEpochSec = options.nowEpochSec;
  const total = state.commentCount;
  return {
    heading:
      total === null
        ? text().comments.heading
        : text().comments.headingWithCount(fmt().exactCount(total) ?? String(total)),
    sort: state.sort,
    sortOptions: (['top', 'new'] as const).map((id) => ({
      id,
      label: text().comments.sort[id],
      // The *requested* order is the selected one, so a forced fallback does not silently move the
      // control the reader set. The notice says what actually happened.
      selected: id === state.requestedSort,
    })),
    threads: state.threads.map((thread) => toThreadView(thread, nowEpochSec)),
    notice: noticeOf(state),
    phase: state.phase,
    loadingMore: state.loadingMore,
    canLoadMore: state.continuation !== null && state.phase === 'ready',
    emptyText: emptyTextOf(state, options.live),
    failureText: state.phase === 'failed' ? failureTextOf(state.failure) : null,
    loadMoreFailureText: state.phase === 'ready' ? failureTextOf(state.failure) : null,
    retryable: state.failure?.kind === 'error' ? state.failure.retryable : false,
  };
}
